// Shows Yotpo star ratings on product cards.
// The Yotpo free plan does not sync ratings to Shopify metafields, so the
// card loads them from the Yotpo API. One request gets the ratings of all
// products. The result stays in sessionStorage for one hour.
if (!customElements.get('yotpo-card-rating')) {
  const CACHE_KEY = 'yotpo-card-ratings';
  const CACHE_TTL = 60 * 60 * 1000;
  const PAGE_SIZE = 100;
  const MAX_PAGES = 20;
  const RATING_MAX = 5;
  const requests = {};

  const readCache = (appKey) => {
    try {
      const cache = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cache && cache.appKey === appKey && Date.now() - cache.time < CACHE_TTL) return cache.ratings;
    } catch (error) {}
    return null;
  };

  const writeCache = (appKey, ratings) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ appKey, ratings, time: Date.now() }));
    } catch (error) {}
  };

  // Returns { [productId]: [score, reviewCount] } for products with at least one review.
  const fetchRatings = async (appKey) => {
    const ratings = {};
    for (let page = 1; page <= MAX_PAGES; page++) {
      const response = await fetch(
        `https://api.yotpo.com/v1/apps/${encodeURIComponent(appKey)}/bottom_lines?count=${PAGE_SIZE}&page=${page}`
      );
      if (!response.ok) throw new Error(`Yotpo bottom_lines request failed: ${response.status}`);
      const bottomlines = (await response.json()).response.bottomlines;
      bottomlines.forEach(({ domain_key, product_score, total_reviews }) => {
        if (total_reviews > 0) ratings[domain_key] = [product_score, total_reviews];
      });
      if (bottomlines.length < PAGE_SIZE) break;
    }
    return ratings;
  };

  const getRatings = (appKey) => {
    if (!requests[appKey]) {
      const cached = readCache(appKey);
      requests[appKey] = cached
        ? Promise.resolve(cached)
        : fetchRatings(appKey).then((ratings) => {
            writeCache(appKey, ratings);
            return ratings;
          });
    }
    return requests[appKey];
  };

  customElements.define(
    'yotpo-card-rating',
    class YotpoCardRating extends HTMLElement {
      connectedCallback() {
        const { appKey, productId } = this.dataset;
        if (!appKey || !productId) return;

        getRatings(appKey)
          .then((ratings) => {
            const rating = ratings[productId];
            if (rating) this.render(rating[0], rating[1]);
          })
          .catch((error) => console.error(error));
      }

      // Uses the same rounding as the Liquid rating markup in sections/main-product.liquid.
      render(score, reviewCount) {
        const rating = Math.round(score * 10) / 10;
        const decimal = rating % 1;
        let ratingDecimal = 0;
        if (decimal > 0.7) {
          ratingDecimal = 1;
        } else if (decimal >= 0.3) {
          ratingDecimal = 0.5;
        }
        const ratingText = rating.toFixed(1);
        const maxText = RATING_MAX.toFixed(1);

        const star = this.querySelector('.rating-star');
        star.style.setProperty('--rating', Math.floor(rating));
        star.style.setProperty('--rating-max', RATING_MAX);
        star.style.setProperty('--rating-decimal', ratingDecimal);

        this.querySelector('.rating').setAttribute(
          'aria-label',
          this.dataset.labelTemplate.replace('[value]', ratingText).replace('[max]', maxText)
        );
        this.querySelector('[data-rating-text]').textContent = `${ratingText} / ${maxText}`;
        this.querySelector('[data-rating-count]').textContent = `(${reviewCount})`;
        this.querySelector('[data-rating-count-label]').textContent = reviewCount;
        this.hidden = false;
      }
    }
  );
}
